/**
 * Money is an integer number of PAISE. Rupees exist only at the display edge.
 *
 * Mirror of jothilingaBackend/services/money.js — keep the two in step.
 *
 * Why: the schema held money in 12 different column types, 18 of them FLOAT.
 * FLOAT keeps ~7 significant digits, so it rounds silently — two live
 * ledger_transaction rows had already drifted 80 paise because Rs.4,28,20,300
 * expressed in paise is not representable in float32. int(11) capped a loan at
 * Rs.214 crore, and decimal(10,2) capped a day-end total at Rs.99,99,999.99.
 *
 * THE RULE: paise crosses the API; rupees never do. Conversion happens at
 * exactly two edges — the form input (toPaise) and the rendered cell
 * (formatINR). Anything in between is paise.
 */

/**
 * Rupees the operator typed -> paise for the API.
 *
 * Math.round, never Math.trunc or `| 0`. Binary floating point makes
 * 12345.67 * 100 === 1234566.9999999999, so truncating loses a paisa on
 * perfectly ordinary values. `| 0` is worse — a 32-bit operation, so it wraps
 * anything above Rs.2.14 crore in paise.
 */
export const toPaise = (rupees) => Math.round(Number(rupees || 0) * 100);

/**
 * Paise from the API -> rupees. For display, and for prefilling a form input
 * the operator edits in rupees. Never send the result back to the API.
 */
export const toRupees = (paise) => Number(paise || 0) / 100;

/**
 * The only way a money figure should reach the screen.
 *
 * Takes PAISE. Passing rupees here shows a figure 100x too small — the mirror
 * of the Day-End Ledger bug that started this work, where
 * `inr(record.amount || 0 / 100)` parsed as `record.amount || (0/100)` and
 * never divided at all.
 */
export const formatINR = (paise) =>
  toRupees(paise).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * formatINR with the rupee sign, for labels and summary boxes.
 */
export const formatINRSymbol = (paise) => `₹${formatINR(paise)}`;

/**
 * Sum paise values exactly. Documents that the values are paise, and guards
 * against a rupee value being mixed into a total by mistake.
 */
export const sumPaise = (values) =>
  (values || []).reduce((total, v) => total + Math.round(Number(v || 0)), 0);
